import React from 'react'
import TextInput from '../../components/FormElements/TextInput'
import { FieldValues, useForm } from 'react-hook-form'
import { ReactComponent as Logo } from './../../images/logo_vertical_colored.svg'
import { Button } from '@demandlocal/components'
import { useLocation, useNavigate } from 'react-router'
import { useAuthLogin } from '../../api/auth/useAuthLogin'
import { useDispatch } from 'react-redux'
import { setAuthenticated, setUser } from '../../store/reducers/userReducer'
import { Link } from 'react-router-dom'
import { useDealershipStore } from '../../store/zustand/dealershipStore'
import { GA4 } from '../../lib/utils/ga4'
import mtm from '../../lib/utils/mtm'
import { Dealership } from '../../lib/types/Dealership'
import { motion } from 'framer-motion'
import toast from 'react-hot-toast'
import { queryClient } from '../../api/queryClient'

const AuthLogin = (): JSX.Element => {
  const navigate = useNavigate()
  const { search } = useLocation()
  const {
    control,
    handleSubmit,
    clearErrors,
    formState: { isSubmitting },
  } = useForm({
    mode: 'onSubmit',
  })

  const { mutateAsync: authLogin } = useAuthLogin()
  const dispatch = useDispatch()
  const setDealership = useDealershipStore((state) => state.setDealership)

  const onSubmit = async (data: Partial<FieldValues>): Promise<void> => {
    const urlSearchParams = new URLSearchParams(search)
    const nextUrl = urlSearchParams.get('next')
    await authLogin({
      email: data.email,
      password: data.password,
      next: nextUrl,
    })
      .then(async (response) => {
        const { data, two_factor_enabled, next } = response as {
          data: {
            email: string
            id: number
            first_name: string
            last_name: string
            terms_accepted_date?: string
            preferred_2fa_method?: string | null
            can_access_billing?: boolean
            organizations: Array<{
              assigned_dealerships_count: number
              dealerships: Array<Dealership>
              has_all_dealerships_assigned: boolean
              id: number
              name: string
              total_dealerships_count: number
            }>
          }
          two_factor_enabled: boolean
          next: string
        }
        if (two_factor_enabled) {
          const preferredTwoFactorMethod = data?.preferred_2fa_method || 'authenticator'
          navigate(`/auth/mfa/${preferredTwoFactorMethod}`, {
            state: {
              user: data,
              next,
            },
          })
        } else {
          if (data.terms_accepted_date) {
            await queryClient.resetQueries(['currentUser'])
            await dispatch(setUser(data))
            await dispatch(setAuthenticated(true))
            const organizations = data?.organizations ?? []
            const dealership = organizations[0]?.dealerships?.[0]
            GA4.configureUser(data)
            GA4.sendEvent({
              event_name: 'login_success',
              event_params: {},
            })
            mtm.sendEvent('Login', 'Success', data.email)
            setDealership(dealership)
            navigate('/dashboard')
            if (next) {
              navigate(next)
              return
            }
          } else {
            navigate('../terms-and-conditions', {
              state: {
                user: data,
                next,
              },
            })
          }
        }
      })
      .catch(async (err) => {
        toast.error(err.response?.data?.error ?? 'Login Failed', {
          duration: 5000,
        })
        await GA4.sendEvent({
          event_name: 'login_failed',
          event_params: {
            error: err.response?.data?.error ?? 'Unknown error',
          },
        })
        mtm.sendEvent('Login', 'Fail')
      })
  }

  return (
    <motion.div
      layout={'size'}
      className="min-h-screen w-full flex justify-center px-4 py-8 md:py-0">
      <motion.div
        layout={'preserve-aspect'}
        className="flex flex-col md:flex-row items-center justify-center md:justify-between w-full max-w-6xl gap-8 md:gap-12">
        <motion.div
          layout={'position'}
          layoutId={'auth-logo'}
          className="flex justify-center md:w-1/3 lg:w-1/4">
          <Logo className="h-24 md:h-32" />
        </motion.div>
        <motion.div
          layout={'preserve-aspect'}
          layoutId={'data-container'}
          className="w-full md:w-2/3 lg:w-1/2 flex flex-col gap-4">
          <motion.div className="p-6 md:p-10 rounded-lg bg-cultured-gray h-fit shadow-xl">
            <div className="font-Spartan font-bold text-xl">Log in to your account</div>
            <div className="flex flex-col gap-y-3 mt-4">
              <a
                href={`${
                  import.meta.env.VITE_API_URL
                }/auth/google?flow=signup&scope=openid%20email%20profile`}
                className="w-full flex items-center font-Spartan font-normal justify-center gap-x-3 py-2.5 border rounded-xl hover:bg-gray-50 duration-150 active:bg-gray-100 bg-white">
                <svg
                  viewBox="0 0 16 16"
                  xmlns="http://www.w3.org/2000/svg"
                  preserveAspectRatio="xMinYMin"
                  className="w-4 h-4">
                  <g fill="none" fillRule="evenodd">
                    <path
                      d="M8 3.093c1.502 0 2.516.65 3.093 1.191l2.258-2.204C11.964.791 10.16 0 8 0A7.994 7.994 0 00.853 4.409L3.44 6.418C4.089 4.488 5.884 3.093 8 3.093z"
                      fill="#EA4335"></path>
                    <path
                      d="M15.68 8.178c0-.658-.053-1.138-.169-1.636H8v2.97h4.409c-.089.737-.569 1.848-1.636 2.595l2.525 1.955c1.51-1.395 2.382-3.449 2.382-5.884z"
                      fill="#4285F4"></path>
                    <path
                      d="M3.449 9.582A4.925 4.925 0 013.182 8c0-.551.098-1.084.258-1.582L.853 4.408A8.007 8.007 0 000 8c0 1.289.311 2.507.853 3.591L3.45 9.582z"
                      fill="#FBBC05"></path>
                    <path
                      d="M8 16c2.16 0 3.973-.711 5.298-1.938l-2.525-1.955c-.675.47-1.582.8-2.773.8-2.116 0-3.911-1.396-4.551-3.325l-2.587 2.01C2.178 14.203 4.872 16 8 16z"
                      fill="#34A853"></path>
                    <path d="M0 0h16v16H0V0z"></path>
                  </g>
                </svg>
                Continue with Google
              </a>
            </div>
            <div className="flex flex-col items-center mt-6 mb-4 text-web-gray">or</div>
            <form
              onSubmit={(e) => {
                clearErrors()
                handleSubmit(onSubmit)(e)
              }}>
              <div className="space-y-4 mt-6">
                <TextInput
                  rules={{ required: 'This field is required' }}
                  name={'email'}
                  label={'Email'}
                  control={control}
                />
                <TextInput
                  rules={{ required: 'This field is required' }}
                  name={'password'}
                  label={'Password'}
                  type={'password'}
                  control={control}
                />
              </div>
              <div className="flex flex-row justify-between mt-4">
                <div className="font-bold text-xs">
                  No account?{' '}
                  <Link
                    className="text-primary-persian-blue font-bold text-xs"
                    to={'../sign-up'}>
                    Create one.
                  </Link>
                </div>
                <Link
                  className="text-primary-xiketic font-bold text-xs"
                  to={'../forgot-password'}>
                  Forgot password?
                </Link>
              </div>
              <div className="pt-8">
                <Button disabled={isSubmitting} type={'submit'}>
                  Login
                </Button>
              </div>
            </form>
          </motion.div>
          <div className="w-full items-center flex justify-center px-4 text-center">
            <span className="text-xs text-primary-xiketic opacity-50">
              By continuing, you agree to our{' '}
              <Link className="font-bold text-xs" to={'/terms-and-conditions'}>
                Terms of Use
              </Link>{' '}
              and{' '}
              <Link className="font-bold text-xs" to={'/privacy-notice'}>
                Privacy Policy
              </Link>
              .
            </span>
          </div>
        </motion.div>
      </motion.div>
    </motion.div>
  )
}

export default AuthLogin
